import React, {useEffect, useState} from "react";
import {Alert, Button, Form, Modal, Spinner,} from "react-bootstrap";
import {useLocation, useNavigate} from "react-router-dom";
import {authenticate, getTokenFromSession} from "../services/authenticate";
import config from "../Config/config";
import axiosInstance from "../axiosInstance";
import "../css/LoginPage.css";
import {useSelector} from "react-redux";
import ForgetPasswordModal from "./ForgetPassword";
import SignupPage from "./SignUp";

const LoginPage = ({show, handleClose}) => {
    const [formData, setFormData] = useState({
        phoneNumber: "",
        password: "",
    });

    const [errors, setErrors] = useState({
        phoneNumberErr: "",
        passwordErr: "",
        loginErr: "",
    });

    const location = useLocation();
    const [resendOtpTimer, setResendOtpTimer] = useState(60);
    const [errorMessage, setErrorMessage] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [showForgetPassword, setShowForgetPassword] = useState(false);
    const [showSignup, setShowSignup] = useState(false);
    const borrowBooks = useSelector((state) => state.BorrowCart.items) || [];
    const buyingBooks = useSelector((state) => state.BuyCart.items) || [];
    useEffect(() => {
        if (step === 2 && resendOtpTimer > 0) {
            const timer = setTimeout(() => {
                setResendOtpTimer((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendOtpTimer, step]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const validateForm = () => {
        let phoneNumberErr = "";
        let passwordErr = "";

        if (formData.phoneNumber === "")
            phoneNumberErr = "Phone Number is required";
        if (formData.password === "") passwordErr = "Password is required";

        if (phoneNumberErr || passwordErr) {
            setErrors({...errors, phoneNumberErr, passwordErr});
            return false;
        }
        return true;
    };

    const handleOtpSubmit = () => {
        if (!otp) {
            setErrorMessage("Please enter the OTP");
            return;
        }
        axiosInstance
            .post(`${config.api.otp}`, {
                phone_number: formData.phoneNumber,
                otp: otp,
            })
            .then((res) => {
                if (res.status === 202) {
                    location.reload();
                }
            })
            .catch((err) => {
                setErrorMessage(err.response?.data || "An unexpected error occurred");
            });
    };

    const sendOtp = (phone) => {
        axiosInstance
            .get(`${config.api.otp}?number=${phone}`)
            .then((res) => {
                if (res.status === 200) {
                    setResendOtpTimer(60);
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({phoneNumberErr: "", passwordErr: "", loginErr: ""});
        setLoading(true);

        let formattedPhoneNumber = formData.phoneNumber.trim();
        if (!formattedPhoneNumber.startsWith("+91")) {
            formattedPhoneNumber = `+91${formattedPhoneNumber}`;
        }
        setFormData({...formData, phoneNumber: formattedPhoneNumber});

        if (validateForm()) {
            try {
                await authenticate(formattedPhoneNumber, formData.password);
                localStorage.setItem("user", formData.phoneNumber);
                const token = getTokenFromSession();

                const addAllItemsToCart = async (token) => {
                    try {
                        for (const items of borrowBooks) {
                            await addItemToCart(items, "LENDING", token);
                        }

                        for (const items of buyingBooks) {
                            await addItemToCart(items, "PURCHASE", token);
                        }
                    } catch (error) {
                        console.error("Error in adding all items to cart:", error.message);
                    }
                };

                await addAllItemsToCart(token);
                handleClose();
            } catch (err) {
                if (err.message === "User is not confirmed.") {
                    setErrors({
                        ...errors,
                        loginErr:
                            "The user already exists but is not verified. If this account belongs to you, please verify it to proceed.",
                    });
                } else {
                    setErrors({
                        ...errors,
                        loginErr: "Incorrect username or password.",
                    });
                }
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    };

    const addItemToCart = async (items, transactionType, token) => {
        try {
            const response = await fetch(`${config.api.cart}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: ` ${token}`,
                },
                body: JSON.stringify({
                    stock_id: items.stockId,
                    isbn: items.isbn,
                    transaction_type: transactionType,
                    lending_days_count: transactionType === "LENDING" ? 1 : 0,
                }),
            });

            if (!response.ok) {
                console.error(
                    "Error with transaction:",
                    transactionType,
                    response.status
                );
                return;
            }

            console.log("Item successfully added to cart:", items.isbn);
        } catch (error) {
            console.error("Error sending item to cart:", error.message);
        }
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} centered className="login-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{step === 1 ? "Login" : "OTP Verification"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {step === 1 && (
                        <>
                            {errors.loginErr && (
                                <Alert variant="danger">{errors.loginErr}</Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="formphoneNumber">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="Phone Number"
                                        isInvalid={!!errors.phoneNumberErr}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phoneNumberErr}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="formPassword">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Password"
                                        isInvalid={!!errors.passwordErr}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.passwordErr}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {errors.loginErr.includes("The user already exists") ? (
                                    <>
                                        <Button type="submit" className="mt-3" disabled={loading}>
                                            {loading ? (
                                                <Spinner animation="border" size="sm"/>
                                            ) : (
                                                "Login"
                                            )}
                                        </Button>
                                        <Button
                                            className="mt-3"
                                            onClick={() => {
                                                setStep(2);
                                                sendOtp(formData.phoneNumber);
                                            }}
                                        >
                                            Verify
                                        </Button>
                                    </>
                                ) : (
                                    <Button type="submit" className="mt-3" disabled={loading}
                                            style={{
                                                backgroundColor: "blue",
                                                color: "white",
                                                borderColor: "none",
                                                borderRadius: "5px",
                                                marginTop: "10px",
                                                marginBottom: "10px"
                                            }}>
                                        {loading ? (
                                            <Spinner animation="border" size="sm"/>
                                        ) : (
                                            "Login"
                                        )}
                                    </Button>
                                )}
                            </Form>
                            <div className="d-flex justify-content-between flex-column  mt-3">
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "blue",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setShowForgetPassword(true);
                                        handleClose();
                                    }}
                                >
                                    Forgot Password?
                                </button>
                                <div style={{marginTop: "10px"}}></div>
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "blue",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setShowSignup(true);
                                        handleClose();
                                    }}
                                >
                                    New here? Sign up to get started!
                                </button>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                            <Form>
                                <p>
                                    The OTP will be sent to this number: {formData.phoneNumber}
                                </p>
                                <Form.Group controlId="otp">
                                    <Form.Label>OTP</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                    />
                                </Form.Group>
                                <Button className="mt-3" onClick={handleOtpSubmit}>
                                    Verify OTP
                                </Button>
                            </Form>
                            {resendOtpTimer === 0 ? (
                                <Button
                                    variant="link"
                                    className="mt-3"
                                    onClick={() => sendOtp(formData.phoneNumber)}
                                >
                                    Resend OTP
                                </Button>
                            ) : (
                                <p className="text-muted mt-3">
                                    Resend OTP in {resendOtpTimer} seconds
                                </p>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
            <ForgetPasswordModal
                show={showForgetPassword}
                handleClose={() => setShowForgetPassword(false)}
            />
            <SignupPage show={showSignup} handleClose={() => setShowSignup(false)}/>
        </>
    );
};

export default LoginPage;
